import React, { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { Upload, Camera, X, Check, RotateCcw } from 'lucide-react';
import { processImage, validateImageFile, createImagePreview, CropArea } from '../utils/imageProcessing';

interface ProfilePictureUploadProps {
  currentImageUrl?: string;
  onImageProcessed: (processedImageUrl: string) => void;
  onCancel: () => void;
  userName: string;
  isUploading?: boolean;
}

interface CropperState {
  crop: { x: number; y: number };
  zoom: number;
  rotation: number;
  croppedAreaPixels?: CropArea;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentImageUrl,
  onImageProcessed,
  onCancel,
  userName,
  isUploading = false
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(currentImageUrl || '');
  const [showCropper, setShowCropper] = useState(false);
  const [cropperState, setCropperState] = useState<CropperState>({
    crop: { x: 0, y: 0 },
    zoom: 1,
    rotation: 0
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    
    // Validate the file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setShowCropper(true);
  }, []);

  const handleCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCropperState(prev => ({
        ...prev,
        croppedAreaPixels
      }));
    },
    []
  );

  const handleCropChange = useCallback((crop: { x: number; y: number }) => {
    setCropperState(prev => ({ ...prev, crop }));
  }, []);

  const handleZoomChange = useCallback((zoom: number) => {
    setCropperState(prev => ({ ...prev, zoom }));
  }, []);

  const handleRotationChange = useCallback((rotation: number) => {
    setCropperState(prev => ({ ...prev, rotation }));
  }, []);

  const resetCropper = useCallback(() => {
    setCropperState({
      crop: { x: 0, y: 0 },
      zoom: 1,
      rotation: 0
    });
  }, []);

  const processCroppedImage = useCallback(async () => {
    if (!selectedFile || !cropperState.croppedAreaPixels) return;

    setIsProcessing(true);
    setError('');

    try {
      const processedImageUrl = await processImage(selectedFile, {
        cropArea: cropperState.croppedAreaPixels,
        zoom: cropperState.zoom,
        targetWidth: 512,
        targetHeight: 512,
        quality: 0.9
      });

      onImageProcessed(processedImageUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process image');
    } finally {
      setIsProcessing(false);
    }
  }, [selectedFile, cropperState, onImageProcessed]);

  const handleCancel = useCallback(() => {
    if (previewUrl && previewUrl !== currentImageUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(currentImageUrl || '');
    setShowCropper(false);
    setError('');
    onCancel();
  }, [previewUrl, currentImageUrl, onCancel]);

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              Upload Profile Picture for {userName}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isProcessing || isUploading}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {!showCropper && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-blue-400 transition-colors">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="flex space-x-2">
                      <Upload className="w-8 h-8 text-gray-400" />
                      <Camera className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <button
                        onClick={triggerFileInput}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        disabled={isUploading}
                      >
                        Choose Image
                      </button>
                      <p className="text-gray-500 text-sm mt-2">
                        JPG, PNG, GIF up to 10MB
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {currentImageUrl && (
                <div className="text-center">
                  <p className="text-gray-600 mb-3">Current Profile Picture:</p>
                  <img
                    src={currentImageUrl}
                    alt={`${userName} current profile picture`}
                    className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-gray-200"
                  />
                </div>
              )}
            </div>
          )}

          {showCropper && previewUrl && (
            <div className="space-y-6">
              <div className="relative h-96 bg-gray-100 rounded-lg overflow-hidden">
                <Cropper
                  image={previewUrl}
                  crop={cropperState.crop}
                  zoom={cropperState.zoom}
                  rotation={cropperState.rotation}
                  aspect={1}
                  onCropChange={handleCropChange}
                  onCropComplete={handleCropComplete}
                  onZoomChange={handleZoomChange}
                  onRotationChange={handleRotationChange}
                  showGrid={true}
                  cropShape="round"
                />
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zoom: {Math.round(cropperState.zoom * 100)}%
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={cropperState.zoom}
                    onChange={(e) => handleZoomChange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rotation: {Math.round(cropperState.rotation)}°
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={360}
                    step={1}
                    value={cropperState.rotation}
                    onChange={(e) => handleRotationChange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={resetCropper}
                  className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={isProcessing || isUploading}
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Reset</span>
                </button>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowCropper(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isProcessing || isUploading}
                  >
                    Back
                  </button>
                  <button
                    onClick={processCroppedImage}
                    disabled={isProcessing || isUploading || !cropperState.croppedAreaPixels}
                    className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing || isUploading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{isProcessing ? 'Processing...' : 'Uploading...'}</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        <span>Save Picture</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfilePictureUpload; 